import React from "react";
import eventsAPI from "restApi/eventsApi";
import { fetchSeen } from "restApi/seenApi";
import { setBadgeSource } from "utils/appBadge";
import { closeDeliveredNotifications } from "utils/notifications";
import useSeenTime from "hooks/useSeenTime";
import { LAST_SEEN_UPCOMING_AT_KEY } from "../CONSTANTS";

// A couple of minutes strikes a balance between the badge feeling live
// and not hammering the API - events don't get added that often.
const POLL_INTERVAL_MS = 2 * 60 * 1000;

// Notifications about events link here (see song_proposals/push.py).
const EVENT_NOTIFICATION_URLS = ["/events", "/upcoming"];

// Polls the upcoming events list app-wide and tracks when the user
// last visited /upcoming - on any of their devices, kept on their
// account (hooks/useSeenTime) - so the Upcoming tab can badge however
// many events are new *or changed* since then. Checks again whenever the
// app comes back into view, so looking at Upcoming on one device clears
// the dot on the others. `enabled` should be `user.isAuthenticated` - no
// point polling while logged out.
export default function useUpcomingEvents(enabled) {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Seen somewhere else since this device last knew: this device's icon badge
  // and any leftover event notifications are stale now.
  const onAdvanced = React.useCallback(() => {
    setBadgeSource("events", false);
    closeDeliveredNotifications(EVENT_NOTIFICATION_URLS);
  }, []);
  const { lastSeenAt, sync, markSeen: recordSeen } = useSeenTime({
    what: "upcoming",
    storageKey: LAST_SEEN_UPCOMING_AT_KEY,
    onAdvanced,
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;

    const poll = () => {
      eventsAPI
        .getUpcomingEvents()
        .then((res) => {
          if (cancelled) return;
          // Same newest-first -> soonest-first reversal Upcoming itself uses.
          setEvents(res.data.slice().reverse());
          setLoading(false);
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
      // Independent of the list: a slow or failed answer here must not hold the
      // events back (the dot just waits for it).
      fetchSeen()
        .then((seen) => {
          if (!cancelled) sync(seen);
        })
        .catch(() => {});
    };

    const pollWhenVisible = () => {
      if (document.visibilityState === "visible") poll();
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", pollWhenVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", pollWhenVisible);
    };
  }, [enabled, sync]);

  // Leaders can create (or edit) an event whose time has already
  // passed, just to keep a record of it - updated_at will be recent
  // even though the event itself is history. Those shouldn't badge
  // (nothing to check out, it was already over by the time this
  // change happened).
  const isPastRecord = (event) =>
    new Date(event.updated_at) >= new Date(event.time_end);

  const newCount = lastSeenAt
    ? events.filter(
        (e) => new Date(e.updated_at) > new Date(lastSeenAt) && !isPastRecord(e)
      ).length
    : 0;

  // Marks the list as seen right now (clears the in-app tab badge, here
  // and - at their next check - on the user's other devices) - anything
  // created after this point is what counts as "new" going forward. Also
  // tells the shared app-icon badge coordinator (see utils/appBadge) events
  // no longer need the installed app's home-screen icon badge, and closes
  // event notifications still sitting in the notification shade. The icon
  // badge itself is set by public/service-worker.js's push handler, not from
  // here, so this only ever releases events' claim on it, never sets one.
  const markSeen = React.useCallback(() => {
    recordSeen();
    setBadgeSource("events", false);
    closeDeliveredNotifications(EVENT_NOTIFICATION_URLS);
  }, [recordSeen]);

  return { events, loading, newCount, markSeen };
}
